import { useModalConfirmation, useToastManager } from "@quortex/front-common"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import PlayPathComposition from "@/App/play/components/features/endpoints/ottEndpoints/PlayPathComposition"
import { useInputList } from "@/queries/playInputs"
import { useOttEndpoint, useUpdateOttEndpoint } from "@/queries/playOttEndpoints"
import { usePool } from "@/queries/playPools"
import { useProcessingList } from "@/queries/playProcessings"
import { useTargetList } from "@/queries/playTargets"

export const useRichOttEndpoint = (ottEndpointUuid: string) => {
  const { data: ottEndpoint } = useSuspenseQuery({
    ...useOttEndpoint({ ottEndpointUuid })
  })
  const { data: inputList } = useSuspenseQuery({
    ...useInputList(),
    select: data => data?.results
  })

  const { data: processingList } = useSuspenseQuery({
    ...useProcessingList(),
    select: data => data.results
  })
  const { data: targetList } = useSuspenseQuery({
    ...useTargetList(),
    select: data => data.results
  })

  const richEndpoint = {
    ...ottEndpoint,
    input: (inputList ?? []).find(input => input.uuid === ottEndpoint.inputUuid),
    processing: (processingList ?? []).find(
      processing => processing.uuid === ottEndpoint.processingUuid
    ),
    target: (targetList ?? []).find(target => target.uuid === ottEndpoint.targetUuid)
  }

  return { richEndpoint }
}

export const useToggleOttEndpoint = (ottEndpointUuid: string) => {
  const { data: pool } = useSuspenseQuery(usePool())
  const { mutateAsync: updateOttEndpoint } = useMutation(useUpdateOttEndpoint({ ottEndpointUuid }))
  const toastManager = useToastManager()
  const { openConfirmationModal } = useModalConfirmation()

  const updateEndpoint = async (enabled: boolean) => {
    try {
      await updateOttEndpoint({ enabled })

      toastManager.success(`Endpoint ${enabled ? "enabled" : "disabled"}`)
    } catch (e) {
      toastManager.error({ apiError: e })
      throw e
    }
  }
  const { richEndpoint } = useRichOttEndpoint(ottEndpointUuid)

  const toggleOttEndpointState = async (enabled: boolean) => {
    if (enabled) {
      updateEndpoint(enabled)
      return
    } else {
      openConfirmationModal({
        children: (
          <>
            Viewers accessing the endpoint path will encounter a 404 error
            <PlayPathComposition
              className="mt-xs"
              endpoint={richEndpoint}
              processingType={pool.type}
            />
          </>
        ),
        okBtnLabel: "Disable",
        onAccept: () => updateEndpoint(enabled),
        title: "Disable endpoint?"
      })
    }
  }

  return { toggleOttEndpointState }
}
